import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, Loader, CheckCircle, FileText } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import PageShell from '../components/PageShell';
import { uploadPdfToDrive } from '../utils/googleDriveUpload';
import {
  SUBJECT_OPTIONS,
  UNIVERSITY_YEAR_OPTIONS,
  subjectLabels,
  universityYearLabels,
} from '../utils/noteLabels';

const FIRAT_UNIVERSITY = 'Fırat Üniversitesi';
const MAX_PDF_MB = 25;

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '12px',
  border: '1px solid rgba(51, 65, 85, 0.35)',
  background: 'rgba(15, 23, 42, 0.5)',
  color: '#e2e8f0',
  fontSize: '14px',
  boxSizing: 'border-box',
};

const NoteUploadPage: React.FC = () => {
  usePageTitle('Not Yükle');
  const { isLoggedIn, openAuthModal } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('matematik');
  const [universityYear, setUniversityYear] = useState('sinif1');
  const [department, setDepartment] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Yalnızca PDF dosyası yükleyebilirsiniz.');
      return;
    }
    if (file.size > MAX_PDF_MB * 1024 * 1024) {
      setError(`Dosya boyutu en fazla ${MAX_PDF_MB} MB olabilir.`);
      return;
    }
    setError(null);
    setPdfFile(file);
    if (!title.trim()) {
      setTitle(file.name.replace(/\.pdf$/i, '').replace(/[_-]+/g, ' ').slice(0, 200));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim() || title.trim().length < 5) {
      setError('Başlık en az 5 karakter olmalı.');
      return;
    }
    if (!description.trim() || description.trim().length < 10) {
      setError('Açıklama en az 10 karakter olmalı.');
      return;
    }
    if (!pdfFile) {
      setError('Lütfen bir PDF dosyası seçin.');
      return;
    }
    if (!legalAccepted) {
      setError('Devam etmek için telif ve kullanım şartlarını kabul etmelisiniz.');
      return;
    }

    setSubmitting(true);
    setUploadProgress(0);
    try {
      const sessionRes = await api.getDriveUploadSession();
      const session = sessionRes.data;
      const { fileId } = await uploadPdfToDrive(pdfFile, session, setUploadProgress);

      await api.createNoteFromDrive({
        fileId,
        fileName: pdfFile.name,
        fileSize: pdfFile.size,
        title: title.trim(),
        description: description.trim(),
        subject,
        universityYear,
        department: department.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Not gönderilemedi.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const uploadStep = !title.trim() || !description.trim() ? 1 : !pdfFile ? 2 : 3;

  if (submitted) {
    return (
      <section style={{ padding: '120px 24px 80px', maxWidth: '560px', margin: '0 auto', zIndex: 2, position: 'relative' }}>
        <div className="glow-card" style={{ padding: '40px 32px', borderRadius: '20px', textAlign: 'center' }}>
          <CheckCircle size={48} color="var(--accent-primary)" style={{ marginBottom: '16px' }} />
          <h1 style={{ margin: '0 0 12px', fontSize: '1.4rem', fontWeight: 800 }}>Teşekkürler!</h1>
          <p style={{ color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 24px' }}>
            Notunuz alındı ve Drive&apos;a yüklendi. Admin onayından sonra sitede yayınlanacak; onaylandığında kutlama bildirimi alacaksınız.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            <Link
              to="/hesabim?tab=pending"
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                textDecoration: 'none',
              }}
            >
              Notlarıma git
            </Link>
            <Link to="/notlar" className="accent-link" style={{ fontSize: '14px', textDecoration: 'none' }}>
              Not arşivine dön
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (!isLoggedIn) {
    return (
      <section style={{ padding: '120px 24px', maxWidth: '480px', margin: '0 auto', textAlign: 'center', zIndex: 2, position: 'relative' }}>
        <div className="glow-card" style={{ padding: '40px 28px', borderRadius: '20px' }}>
          <h1 style={{ margin: '0 0 12px', fontSize: '1.25rem', fontWeight: 800 }}>Giriş gerekli</h1>
          <p style={{ color: '#94a3b8', margin: '0 0 20px', fontSize: '14px' }}>
            Not yüklemek için hesabınıza giriş yapın.
          </p>
          <button
            type="button"
            onClick={() => openAuthModal('login')}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              border: 'none',
              background: 'var(--gradient-primary)',
              color: 'white',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Giriş yap
          </button>
        </div>
      </section>
    );
  }

  return (
    <PageShell
      badge="Topluluk katkısı"
      badgeIcon={Upload}
      title="Not Yükle"
      subtitle="PDF dosyanız doğrudan site Drive klasörüne yüklenir. Üniversite: Fırat Üniversitesi."
      backTo="/notlar"
      backLabel="Tüm notlar"
      maxWidth="720px"
    >
      <div className="upload-steps" aria-hidden>
        <div className={`upload-step${uploadStep >= 1 ? ' upload-step--active' : ''}`} />
        <div className={`upload-step${uploadStep >= 2 ? ' upload-step--active' : ''}`} />
        <div className={`upload-step${uploadStep >= 3 ? ' upload-step--active' : ''}`} />
      </div>
      <p style={{ color: '#64748b', fontSize: '13px', margin: '-16px 0 24px' }}>
        Adım {uploadStep}/3 — {uploadStep === 1 ? 'Not bilgileri' : uploadStep === 2 ? 'PDF seçimi' : 'Gönder'}
      </p>

      <form onSubmit={handleSubmit} className="glow-card" style={{ padding: '28px', borderRadius: '20px' }}>
        <label style={labelStyle}>Başlık *</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} required minLength={5} />

        <label style={{ ...labelStyle, marginTop: '14px' }}>Açıklama *</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          style={{ ...inputStyle, resize: 'vertical' }}
          required
          minLength={10}
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '14px' }}>
          <div>
            <label style={labelStyle}>Ders *</label>
            <select value={subject} onChange={(e) => setSubject(e.target.value)} style={inputStyle}>
              {SUBJECT_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {subjectLabels[s]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Sınıf / yıl *</label>
            <select value={universityYear} onChange={(e) => setUniversityYear(e.target.value)} style={inputStyle}>
              {UNIVERSITY_YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {universityYearLabels[y]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label style={{ ...labelStyle, marginTop: '14px' }}>Üniversite</label>
        <input value={FIRAT_UNIVERSITY} style={{ ...inputStyle, opacity: 0.85 }} readOnly disabled />

        <label style={{ ...labelStyle, marginTop: '14px' }}>Bölüm</label>
        <input value={department} onChange={(e) => setDepartment(e.target.value)} style={inputStyle} placeholder="Opsiyonel — otomatik tahmin de yapılır" />

        <label style={{ ...labelStyle, marginTop: '18px' }}>PDF dosyası *</label>
        <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" onChange={handleFileChange} style={{ display: 'none' }} />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: '12px',
            border: pdfFile ? '1px solid var(--accent-primary-glow)' : '1px dashed rgba(51,65,85,0.5)',
            background: pdfFile ? 'var(--accent-primary-soft)' : 'rgba(15,23,42,0.35)',
            color: '#e2e8f0',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
          }}
        >
          <FileText size={20} color={pdfFile ? 'var(--accent-primary)' : '#64748b'} />
          {pdfFile ? `${pdfFile.name} (${(pdfFile.size / 1024 / 1024).toFixed(2)} MB)` : 'PDF seçin (max 25 MB)'}
        </button>

        {submitting && uploadProgress > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ height: 6, borderRadius: 999, background: 'rgba(51,65,85,0.4)', overflow: 'hidden' }}>
              <div style={{ width: `${uploadProgress}%`, height: '100%', background: 'var(--gradient-logo)', transition: 'width 0.2s' }} />
            </div>
            <p style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>Drive&apos;a yükleniyor: %{uploadProgress}</p>
          </div>
        )}

        {error ? (
          <p className="text-danger" style={{ fontSize: '14px', marginTop: '16px', marginBottom: 0 }}>{error}</p>
        ) : null}

        <label
          className="legal-consent"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            marginTop: '20px',
            cursor: 'pointer',
            fontSize: '13px',
            color: 'var(--text-muted, #94a3b8)',
            lineHeight: 1.55,
          }}
        >
          <input
            type="checkbox"
            checked={legalAccepted}
            onChange={(e) => setLegalAccepted(e.target.checked)}
            style={{ marginTop: '3px', accentColor: 'var(--accent-primary)' }}
          />
          <span>
            Yüklediğim içeriğin paylaşım hakkına sahip olduğumu;{' '}
            <Link to="/terms" className="accent-link">Kullanım Şartları</Link> ve{' '}
            <Link to="/telif" className="accent-link">Telif ve İçerik Politikası</Link> metinlerini okuduğumu kabul
            ediyorum.
          </span>
        </label>

        <motion.button
          type="submit"
          disabled={submitting || !legalAccepted}
          whileTap={{ scale: 0.98 }}
          className="btn-primary"
          style={{
            marginTop: '16px',
            width: '100%',
            padding: '14px',
            borderRadius: '12px',
            border: 'none',
            fontWeight: 700,
            fontSize: '15px',
            cursor: submitting || !legalAccepted ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            opacity: submitting || !legalAccepted ? 0.65 : 1,
          }}
        >
          {submitting ? <Loader size={18} className="spin" /> : <Upload size={18} />}
          {submitting ? 'Yükleniyor...' : 'Onaya gönder'}
        </motion.button>
      </form>
    </PageShell>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: '#94a3b8',
  marginBottom: '6px',
};

export default NoteUploadPage;
